import { startTransition, useState } from 'react';
import { isApprovalByTeamEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { type MultiComboBoxProps, MultiComboBox } from '@gonfalon/launchpad-experimental/MultiComboBox';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { Collection, Header, ListBoxSection, Tag } from '@launchpad-ui/components';

import { MemberListItem, TeamListItem } from './internal/MaintainerDialog';
import type { MaintainerComboBoxItem, MultipleMaintainerProps, SingleMaintainerProps } from './internal/types';
import type { UseMaintainerItemsParams } from './internal/useMaintainerItems';
import { useMaintainerItems } from './internal/useMaintainerItems';
import { useMaintainerSelection } from './internal/useMaintainerSelection';
import { getSelectedValues } from './internal/utils';
import { TeamMaintainerTag } from './TeamMaintainerTag';

import styles from './SelectMaintainerTags.module.css';

interface CommonProps
  extends
    Pick<UseMaintainerItemsParams, 'teamsOnly' | 'membersOnly' | 'accessChecks' | 'filterOutCurrentMember'>,
    Pick<
      MultiComboBoxProps<MaintainerComboBoxItem>,
      'placeholder' | 'label' | 'className' | 'onBlur' | 'isRequired' | 'id' | 'onClick' | 'description' | 'placement'
    > {
  closeOnSelection?: boolean;
  maxVisibleTags?: number;
}

export type SelectMaintainerTagsProps = (SingleMaintainerProps | MultipleMaintainerProps) & CommonProps;

const getSelectedItems = (params: ReturnType<typeof getSelectedValues>) => {
  const { maintainerId, maintainerTeamKey, maintainerIds = [], maintainerTeamKeys = [] } = params;
  const selectedItems = maintainerIds
    .map((id) => ({
      type: 'member',
      id,
    }))
    .concat(
      maintainerTeamKeys.map((id) => ({
        type: 'team',
        id,
      })),
    );
  if (maintainerId !== undefined) {
    selectedItems.push({
      type: 'member',
      id: maintainerId,
    });
  }

  if (maintainerTeamKey !== undefined) {
    selectedItems.push({
      type: 'team',
      id: maintainerTeamKey,
    });
  }
  return selectedItems;
};

export function SelectMaintainerTags(props: SelectMaintainerTagsProps) {
  const {
    filterOutCurrentMember,
    accessChecks,
    teamsOnly,
    label,
    className,
    onBlur,
    isRequired,
    maxVisibleTags = -1,
    placeholder,
    onClick,
    description,
    id,
    placement,
  } = props;
  const [filterText, setFilterText] = useState('');

  const membersOnly = !isApprovalByTeamEnabled();

  const selectedValues = getSelectedValues(props);

  const { itemsInSections, isPending: isDataLoading } = useMaintainerItems({
    ...selectedValues,
    filterText,
    teamsOnly,
    membersOnly,
    accessChecks,
    filterOutCurrentMember,
    suspense: false,
  });
  const selectedItems = getSelectedItems(selectedValues);

  const { handleSelectionChange } = useMaintainerSelection({
    itemsInSections,
    ...props,
  });
  const visibleTags = maxVisibleTags > 0 ? selectedItems.slice(0, maxVisibleTags) : selectedItems;
  const selectedKeys = selectedItems.map((item) => item.id);

  return (
    <MultiComboBox<MaintainerComboBoxItem>
      description={description}
      className={className}
      placeholder={placeholder}
      selectionMode="multiple"
      isRequired={isRequired}
      onClick={onClick}
      id={id}
      tags={() => (
        <>
          {visibleTags.map((tag) => {
            const { type, id: itemId } = tag;
            if (type === 'member') {
              return <MemberMaintainerTag key={itemId} id={`${type}:${itemId}`} maintainerId={itemId} />;
            }
            return <TeamMaintainerTag key={itemId} id={`${type}:${itemId}`} maintainerTeamKey={itemId} />;
          })}
          {visibleTags.length !== selectedItems.length && (
            <Tag id="more-tags" textValue="More tags">
              {() => <>{selectedItems.length - visibleTags.length}</>}
            </Tag>
          )}
        </>
      )}
      label={label}
      selectedKeys={selectedKeys}
      items={itemsInSections}
      filterText={filterText}
      setFilterText={(search) => startTransition(() => setFilterText(search))}
      onSelectionChange={handleSelectionChange}
      renderEmptyState={(inputValue) =>
        isDataLoading ? <p>Loading...</p> : <p>{inputValue ? <>No results found for: {inputValue}</> : 'No options'}</p>
      }
      isLoading={isDataLoading}
      onBlur={onBlur}
      placement={placement}
    >
      {(section) => {
        const hidden = section.id === 'selected' && visibleTags.length === selectedItems.length;

        return (
          <ListBoxSection hidden={hidden} id={section.id}>
            <Header className={styles.menuSection}>{section.name}</Header>
            <Collection items={section.children}>
              {(item) => {
                return item.type === 'team' ? (
                  <TeamListItem key={item.id} {...item} disabled={hidden} includeTeamCount />
                ) : item.type === 'member' ? (
                  <MemberListItem key={item.id} {...item} disabled={hidden} />
                ) : null;
              }}
            </Collection>
          </ListBoxSection>
        );
      }}
    </MultiComboBox>
  );
}
